import TagManager from "react-gtm-module";
import { stripUndefined } from "../js_utils/object_utils";
import { Analytics, EcommerceEvent, ProductProperties } from "./types";

export class GTMSourceProvider {
  initialized: boolean = false;

  initialize(gtmId: string) {
    // There can only be 1 GTM tag
    if (!this.initialized) {
      TagManager.initialize({ gtmId });
      this.initialized = true;
    }

    return gtmAnalytics;
  }
}

export interface GTMClient {
  provider: "gtm";
  gtmId: string;
}

export function makeGTMClient(gtmId: string): GTMClient {
  return {
    provider: "gtm",
    gtmId,
  };
}

const gtmAnalytics: Analytics = {
  group: () => {},
  register: () => {},
  identify: () => {},
  screen: (name) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "pageview",
        page: {
          ...(name && { title: name }),
          path: window.location.pathname,
          href: window.location.href,
        },
      },
    });
  },
  event: (eventName, props) => {
    TagManager.dataLayer({
      dataLayer: {
        event: eventName,
        ...props,
      },
    });
  },
  ecommerceEvent: (event) => {
    sendGTMEcommerceEvent(event);
  },
};

function sendGTMEcommerceEvent(event: EcommerceEvent) {
  switch (event.event) {
    case "Product Viewed":
      TagManager.dataLayer({
        dataLayer: {
          ecommerce: null,
        },
      });
      TagManager.dataLayer({
        dataLayer: {
          event: "eec.detail",
          ecommerce: {
            detail: {
              products: event.products.map(toProduct),
            },
          },
        },
      });
      break;

    case "Checkout Started":
      TagManager.dataLayer({
        dataLayer: {
          ecommerce: null,
        },
      });
      TagManager.dataLayer({
        dataLayer: {
          event: "eec.checkout",
          ecommerce: {
            checkout: {
              actionField: { step: 1 },
              products: event.products.map(toProduct),
            },
          },
        },
      });

      break;
    case "Order Completed":
      TagManager.dataLayer({
        dataLayer: {
          ecommerce: null,
        },
      });
      TagManager.dataLayer({
        dataLayer: {
          event: "eec.purchase",
          ecommerce: {
            currencyCode: event.currency,
            purchase: {
              actionField: stripUndefined({
                id: event.id,
                affiliation: event.affiliation,
                revenue: event.revenue
                  ? String(event.revenue.toFixed(2))
                  : undefined,
                tax: event.tax ? String(event.tax.toFixed(2)) : undefined,
                shipping: event.shipping
                  ? String(event.shipping.toFixed(2))
                  : undefined,
                coupon: event.coupon,
              }),
              products: event.products.map(toProduct),
            },
          },
        },
      });
      break;
    default:
      console.error(`Analytics: event ${event.event} not supported yet.`);
      return ["", {}];
  }
}

function toProduct(p: ProductProperties) {
  return stripUndefined({
    id: p.id,
    sku: p.sku,
    name: p.name,
    category: p.category,
    brand: p.brand,
    variant: p.variant,
    list: p.list,
    position: p.position,
    price: p.price ? String(p.price.toFixed(2)) : undefined,
    quantity: p.quantity,
    coupon: p.coupon,
  });
}
